import { useEffect } from 'react'
import { Outlet, ScrollRestoration, useLocation } from 'react-router'
import { MfaProvider } from '@/features/auth/mfa/MfaProvider'
import { AnalyticsConsentBanner } from '@/features/legal/components/AnalyticsConsentBanner'
import { useAnalyticsConsentSync } from '@/features/legal/useAnalyticsConsentSync'
import { PAGE_VIEW_SETTLE_MS, routeInfo, setAnalyticsContext, syncReplayWithUrl, trackPageView } from '@/lib/analytics'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { useUpdatePrompt } from '@/lib/pwa'
import { useTheme } from '@/lib/theme'
import { scrollKey } from './shell-utils'

/** Top of the route tree: scroll restoration, page views and the analytics choice (docs/analytics.md), the new
 *  version prompt and the two factor prompts (G-03). */
export function RootLayout() {
  const location = useLocation()
  const { me } = useAuth()
  const { lang } = useI18n()
  const { preference } = useTheme()
  useUpdatePrompt()
  useAnalyticsConsentSync()
  const community = routeInfo(location.pathname).community
  const role = (community && me?.communities.find((c) => c.slug.toLowerCase() === community && c.status === 'active')?.role) || null
  // the route's super properties and the replay check at once; its page_view once the route stayed a moment, so a
  // route that redirects at once (the home page to the last community, a sign in guard) is not a page someone saw
  useEffect(
    () => trackPageView(location.pathname, role, PAGE_VIEW_SETTLE_MS),
    // the role only changes the super property, not the page: no page view of its own
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname],
  )
  useEffect(() => setAnalyticsContext({ community_role: role, logged_in: !!me }), [role, me])
  useEffect(() => setAnalyticsContext({ locale: lang, theme: preference }), [lang, preference])
  // session replay pauses while the address bar holds more than safe query parameters and resumes once it is clean
  useEffect(() => syncReplayWithUrl(), [location.search, location.hash])
  return (
    <>
      <Outlet />
      <ScrollRestoration getKey={scrollKey} />
      <MfaProvider />
      <AnalyticsConsentBanner />
    </>
  )
}
